export type DriveKind = 'Photo' | 'Video' | 'Audio' | 'Document';
export interface DriveFile {
  id: string;
  name: string;
  kind: DriveKind;
  path: string;
  sizeMb: number;
  modified: string;
  image?: string;
}
export interface DriveFolder {
  path: string;
  name: string;
  children: string[];
  files: DriveFile[];
}

const unsplashPreviewUrl = (photoId: string) =>
  `https://images.unsplash.com/photo-${photoId}?w=300&q=60`;

const brzozaPreviewImages = [
  '1570968915860-54d5c301fa9f',
  '1442512595331-e89e73853f31',
  '1495474472287-4d71bcdd2085',
  '1517248135467-4c7edcad34c4',
  '1554118811-1e0d58224f24',
  '1501339847302-ac426a4a7cbb',
  '1488477181946-6428a0291777',
  '1453614512568-c4024d13c247',
].map(unsplashPreviewUrl);
const lipaPreviewImages = [
  '1544787219-7f47ccb76574',
  '1509365465985-25d11c17e812',
  '1509440159596-0249088772ff',
  '1511192336575-5a79af67a629',
].map(unsplashPreviewUrl);

function demoFileSizeInMegabytes(kind: DriveKind, fileIndex: number): number {
  switch (kind) {
    case 'Video':
      return 120 + ((fileIndex * 37) % 900);
    case 'Audio':
      return 6 + (fileIndex % 9);
    case 'Document':
      return 1 + (fileIndex % 4);
    default:
      return 3 + ((fileIndex * 7) % 18);
  }
}

function demoModifiedDate(fileIndex: number): string {
  const dayOfMonth = 1 + ((fileIndex * 3) % 28);
  const month = fileIndex % 2 ? 'Oct' : 'Nov';
  return `${dayOfMonth} ${month}`;
}

function demoFiles(
  path: string,
  namePrefix: string,
  count: number,
  kind: DriveKind,
  previewImages: string[],
  extension: string
): DriveFile[] {
  const hasPreview = kind === 'Photo' || kind === 'Video';
  return Array.from({ length: count }, (_, fileIndex) => ({
    id: `${path}/${fileIndex}`,
    name: `${namePrefix}_${String(fileIndex + 1).padStart(3, '0')}.${extension}`,
    kind,
    path,
    sizeMb: demoFileSizeInMegabytes(kind, fileIndex),
    modified: demoModifiedDate(fileIndex),
    image: hasPreview ? previewImages[fileIndex % previewImages.length] : undefined,
  }));
}

export function driveTree(
  workspaceId: string,
  root: string,
  logos: { file: string; src: string }[] = []
): Record<string, DriveFolder> {
  const clientLogosOrDemoFiles = (path: string, demoLogoFiles: DriveFile[]) =>
    logos.length
      ? logos.map((logo, logoIndex) => ({
          id: `${path}/logo-${logoIndex}`,
          name: logo.file,
          kind: 'Photo' as const,
          path,
          sizeMb: 1,
          modified: '2 Oct',
          image: logo.src,
        }))
      : demoLogoFiles;
  if (workspaceId === 'lipa') {
    const tree: Record<string, DriveFolder> = {
      [root]: {
        path: root,
        name: root,
        children: [`${root}/Zdjęcia`, `${root}/Wydarzenia`],
        files: clientLogosOrDemoFiles(
          root,
          demoFiles(root, 'logo_lipa', 2, 'Photo', lipaPreviewImages, 'png')
        ),
      },
      [`${root}/Zdjęcia`]: {
        path: `${root}/Zdjęcia`,
        name: 'Zdjęcia',
        children: [],
        files: demoFiles(`${root}/Zdjęcia`, 'IMG', 96, 'Photo', lipaPreviewImages, 'jpg'),
      },
      [`${root}/Wydarzenia`]: {
        path: `${root}/Wydarzenia`,
        name: 'Wydarzenia',
        children: [],
        files: demoFiles(`${root}/Wydarzenia`, 'jazz', 22, 'Video', lipaPreviewImages, 'mp4'),
      },
    };
    return tree;
  }
  const photosFolder = `${root}/Photos 2026`;
  return {
    [root]: {
      path: root,
      name: root,
      children: [
        `${root}/Logos`,
        photosFolder,
        `${root}/Videos`,
        `${root}/Menu`,
        `${root}/Archive 2025`,
      ],
      files: [],
    },
    [`${root}/Logos`]: {
      path: `${root}/Logos`,
      name: 'Logos',
      children: [],
      files: clientLogosOrDemoFiles(
        `${root}/Logos`,
        demoFiles(`${root}/Logos`, 'brzoza_logo', 4, 'Photo', brzozaPreviewImages, 'png')
      ),
    },
    [photosFolder]: {
      path: photosFolder,
      name: 'Photos 2026',
      children: [`${photosFolder}/October`, `${photosFolder}/November`],
      files: [],
    },
    [`${photosFolder}/October`]: {
      path: `${photosFolder}/October`,
      name: 'October',
      children: [],
      files: demoFiles(`${photosFolder}/October`, 'IMG', 180, 'Photo', brzozaPreviewImages, 'HEIC'),
    },
    [`${photosFolder}/November`]: {
      path: `${photosFolder}/November`,
      name: 'November',
      children: [],
      files: demoFiles(`${photosFolder}/November`, 'IMG', 210, 'Photo', brzozaPreviewImages, 'jpg'),
    },
    [`${root}/Videos`]: {
      path: `${root}/Videos`,
      name: 'Videos',
      children: [],
      files: demoFiles(`${root}/Videos`, 'clip', 60, 'Video', brzozaPreviewImages, 'mov'),
    },
    [`${root}/Menu`]: {
      path: `${root}/Menu`,
      name: 'Menu',
      children: [],
      files: demoFiles(`${root}/Menu`, 'menu', 2, 'Document', brzozaPreviewImages, 'pdf'),
    },
    [`${root}/Archive 2025`]: {
      path: `${root}/Archive 2025`,
      name: 'Archive 2025',
      children: [],
      files: demoFiles(`${root}/Archive 2025`, 'old', 56, 'Photo', brzozaPreviewImages, 'jpg'),
    },
  };
}

export const countFiles = (tree: Record<string, DriveFolder>) =>
  Object.values(tree).reduce((sum, folder) => sum + folder.files.length, 0);
